import { CloudCredService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/cloud_cred_connect";
import type { CloudCred } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/cloud_cred_pb";
import { useQuery } from "@tanstack/react-query";
import React, { createContext, useContext, useMemo } from "react";
import { createConnectClient } from "server/connect";
import { isActiveCloudCred } from "utils/cloudCreds";

import { OrgContext } from "./OrgContext";

interface ICloudCredContext {
  cloudCreds: CloudCred[];
  isLoading: boolean;
  isError: boolean;
}

export const cloudCredContextDefaultValues: ICloudCredContext = {
  cloudCreds: [],
  isLoading: false,
  isError: false,
};

export const CloudCredContext = createContext<ICloudCredContext>(
  cloudCredContextDefaultValues,
);

const cloudCredClient = createConnectClient(CloudCredService);

const CloudCredContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { activeOrgId } = useContext(OrgContext);
  const {
    data: cloudCreds = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["cloudCreds", activeOrgId],
    queryFn: async () => {
      const response = await cloudCredClient.listCloudCredsForOrg({
        orgId: activeOrgId,
      });
      return response.items.filter(isActiveCloudCred);
    },
    enabled: Boolean(activeOrgId),
    staleTime: 5 * 60 * 1000,
  });
  const contextValue = useMemo(
    () => ({
      cloudCreds,
      isLoading: Boolean(activeOrgId) && isLoading,
      isError,
    }),
    [activeOrgId, cloudCreds, isError, isLoading],
  );

  return (
    <CloudCredContext.Provider value={contextValue}>
      {children}
    </CloudCredContext.Provider>
  );
};

export default CloudCredContextProvider;
